import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import type { DemoAccount } from '../../types/demoAccount';

interface ExtendExpiryDialogProps {
  open: boolean;
  onClose: () => void;
  account: DemoAccount | null;
  onExtend: (additionalDays: number) => void;
  isLoading: boolean;
}

export const ExtendExpiryDialog: React.FC<ExtendExpiryDialogProps> = ({
  open,
  onClose,
  account,
  onExtend,
  isLoading,
}) => {
  const [days, setDays] = useState(30);

  if (!account) return null;

  const currentExpiry = account.demoExpiresAt
    ? new Date(account.demoExpiresAt).toLocaleDateString()
    : 'No expiry set';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Extend Demo Account</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Extending account for <strong>{account.displayName}</strong> ({account.email})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current expiry: {currentExpiry}
          </Typography>
          {account.isDisabled && (
            <Typography variant="body2" color="warning.main">
              This account is currently disabled. Extending will re-enable it.
            </Typography>
          )}
          <TextField
            label="Additional Days"
            type="number"
            value={days}
            onChange={(e) => setDays(Math.max(1, Number(e.target.value)))}
            fullWidth
            slotProps={{
              htmlInput: { min: 1 },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={() => onExtend(days)}
          variant="contained"
          disabled={isLoading || days < 1}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {isLoading ? 'Extending...' : 'Extend'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
